import {
  LINE_ME_URL,
  WHATSAPP_ME_URL,
} from '../../consts/defaultConfigValues/defaultConstants'
import { WorkflowChannel } from '../../graphQL/queries/getSession/types'

interface IGetGoToChannelUrl {
  channel: `${WorkflowChannel}`
  phoneNumber: string
  text?: string
}

const urlResultMap: Record<
  `${WorkflowChannel}`,
  (args: Pick<IGetGoToChannelUrl, 'phoneNumber' | 'text'>) => string
> = {
  [WorkflowChannel.WHATSAPP]: ({ phoneNumber, text = '' }) =>
    `${new URL(phoneNumber, WHATSAPP_ME_URL).toString()}?text=${encodeURIComponent(text)}`,
  [WorkflowChannel.LINE]: ({ phoneNumber, text = '' }) =>
    `${new URL(`${phoneNumber}/${text}`, LINE_ME_URL).toString()}`,
}

/**
 * Whether `getGoToChannelUrl` can build a URL for this channel, or will fall
 * back to WhatsApp.
 *
 * Exported so an event-driven caller can report the fallback without the util
 * emitting telemetry from a render path. `channel` is typed to
 * `WorkflowChannel` but arrives from `sessionData.workflow.channel`, which the
 * server owns, so any other string reaches here.
 *
 * `Object.hasOwn` rather than `in`: `in` walks the prototype chain, so
 * `'constructor'` would report as supported and then resolve to
 * `Object.prototype.constructor` at the lookup below.
 */
export const isSupportedChannel = (channel: string): boolean =>
  Object.hasOwn(urlResultMap, channel)

/**
 * Builds the URL that returns the shopper to their conversation.
 *
 * `channel` is typed to `WorkflowChannel` but arrives from
 * `sessionData.workflow.channel`, which the server owns — so any other truthy
 * string reaches this lookup. Unguarded, `urlResultMap[channel](rest)` threw
 * `TypeError: urlResultMap[channel] is not a function` from whatever handler
 * called it; on the order-confirmation screen that is a click on the only exit
 * from a screen where the shopper's money has already moved.
 *
 * Falls back to WhatsApp rather than throwing or returning empty: it is the
 * default everywhere else in the app, and a link to the wrong-but-working
 * channel beats a dead button.
 *
 * **Reports nothing, deliberately.** `routes/index.tsx` calls this in the
 * render body of the root component, which re-renders on seven different
 * selectors and again under `StrictMode` — so telemetry here would fire per
 * render rather than per occurrence, for every session on an affected
 * storefront, and `datadogRum.startView` would restart the RUM view mid-render
 * and misattribute everything after it. Callers that run from an event report
 * the fallback themselves; see `isSupportedChannel`.
 */
const getGoToChannelUrl = ({ channel, ...rest }: IGetGoToChannelUrl) => {
  const buildUrl = isSupportedChannel(channel)
    ? urlResultMap[channel]
    : urlResultMap[WorkflowChannel.WHATSAPP]

  return buildUrl(rest)
}

export default getGoToChannelUrl
